<template>
    <Teleport to="body">
        <transition name="fade">
            <div 
                v-if="isOpen" 
                class="media-viewer-overlay" 
                @click="handleOverlayClick"
            >
                
                <div class="floating-actions">
                    <a href="javascript:void(0)" class="btn-float" title="Baixar" @click.stop="downloadMedia">
                        <i class="ri-download-line"></i>
                    </a>
                    <button class="btn-float" @click="close" title="Fechar">
                        <i class="ri-close-line"></i>
                    </button>
                </div>

                <button 
                    v-if="isOpen && galleryList.length > 1 && currentIndex > 0"
                    class="carousel-arrow carousel-arrow-left"
                    @click.stop="navigateGallery(-1)"
                >
                    <i class="ri-arrow-left-s-line"></i>
                </button>

                <button 
                    v-if="isOpen && galleryList.length > 1 && currentIndex < galleryList.length - 1"
                    class="carousel-arrow carousel-arrow-right"
                    @click.stop="navigateGallery(1)"
                >
                    <i class="ri-arrow-right-s-line"></i>
                </button>

                <div 
                    class="viewer-body"
                    @touchstart="handleTouchStart"
                    @touchend="handleTouchEnd"
                >
                    <div 
                        v-if="media?.type === 'image'" 
                        class="image-wrapper"
                        @click.stop="toggleZoom"
                        @mousemove="handleMouseMove"
                        @mouseleave="handleMouseLeave"
                    >
                        <img 
                            ref="imageRef"
                            :src="media.url" 
                            class="media-content"
                            :class="{ 'is-zoomed': isZoomed }"
                            :style="zoomStyle"
                        >
                    </div>

                    <video v-liberar-midia
                        v-else-if="media?.type === 'video'"
                        :src="media.url"
                        controls
                        preload="auto"
                        class="media-content video-content"
                        @click.stop
                        @waiting="videoStall.onStall"
                        @stalled="videoStall.onStall"
                        @playing="videoStall.onPlaying"
                        @error="videoStall.onError"
                    ></video>
                </div>

                <div class="gallery-footer" v-if="galleryList.length > 1" @click.stop>
                    <div class="media-caption-container" v-if="media?.caption">
                        {{ media.caption }}
                    </div>
                    <div class="gallery-counter text-center text-white-50 mb-2 font-size-12">
                        {{ currentIndex + 1 }} de {{ galleryList.length }}
                    </div>
                    <div class="thumbnails-container" ref="thumbnailsContainerRef">
                        <div 
                            v-for="(item, idx) in galleryList"
                            :key="getItemKey(item)"
                            class="thumb-wrapper"
                            :class="{ 'active': idx === currentIndex }"
                            @click="selectItem(item, idx)"
                        >
                            <img v-if="getItemType(item) === 'image'" :src="getItemUrl(item)" loading="lazy">
                            
                            <div v-else-if="getItemType(item) === 'video'" class="video-thumb">
                                <VideoDeChat :src="getItemUrl(item)" preload="metadata"></VideoDeChat>
                                <i class="ri-play-circle-fill play-icon"></i>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import VideoDeChat from '@/components/app/layout/chat/VideoDeChat.vue';
import { useUiStore } from '@/stores/app/uiStore';
import { useChatStore } from '@/stores/app/chatStore';
import { useVideoStallRecovery } from '@/composables/useVideoStallRecovery';

const uiStore = useUiStore();
const chatStore = useChatStore();

// Recupera o player quando o download do vídeo trava no meio (stall de rede)
const videoStall = useVideoStallRecovery();

const isZoomed = ref(false);
const imageRef = ref(null);
const thumbnailsContainerRef = ref(null);
const zoomOrigin = ref('center center'); 

// --- COMPUTEDS ---
const isOpen = computed(() => uiStore.isMediaViewerOpen);
const media = computed(() => uiStore.mediaToView);

// Modo galeria = veio de um media_grid com lista injetada
const isGalleryMode = computed(() => !!media.value?.gallery?.length);

const galleryList = computed(() => {
    if (isGalleryMode.value) return media.value.gallery;
    
    // Fallback corrigido: busca por 'imagem', 'video' ou 'file' com mimetype compativel
    return chatStore.messages.filter(m => {
        const mime = m.fileData?.mimeType || '';
        const isImage = m.type === 'imagem' || (m.type === 'file' && mime.startsWith('image/'));
        const isVideo = m.type === 'video'  || (m.type === 'file' && mime.startsWith('video/'));
        return isImage || isVideo;
    });
});

const currentIndex = computed(() => {
    if (!media.value) return -1;
    if (isGalleryMode.value) return media.value.galleryIndex ?? 0;
    return galleryList.value.findIndex(m => m.fileData?.url === media.value.url);
});

// Anima e centraliza o thumbnail ativo na barra inferior
watch(currentIndex, (newIdx) => {
    if (newIdx < 0) return;
    nextTick(() => {
        if (thumbnailsContainerRef.value) {
            const activeThumb = thumbnailsContainerRef.value.children[newIdx];
            if (activeThumb) {
                activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    });
});

const zoomStyle = computed(() => ({
    transform      : isZoomed.value ? 'scale(2.5)' : 'scale(1)',
    transformOrigin: zoomOrigin.value,
    cursor         : isZoomed.value ? 'zoom-out' : 'zoom-in'
}));

const getItemUrl     = (item) => isGalleryMode.value ? item.url : item.fileData?.url;
const getItemType    = (item) => {
    if (isGalleryMode.value) return item.type;
    const mime = item.fileData?.mimeType || '';
    if (item.type === 'video' || mime.startsWith('video/')) return 'video';
    return 'image';
};
const getItemCaption = (item) => isGalleryMode.value ? item.caption : (item.text || item.fileData?.caption);
const getItemKey     = (item) => isGalleryMode.value ? item.url : item.id;

const selectItem = (item, index) => {
    uiStore.openMediaViewer(
        getItemUrl(item),
        getItemType(item),
        getItemCaption(item) ?? '',
        isGalleryMode.value ? media.value.gallery : undefined,
        isGalleryMode.value ? index : undefined
    );
    resetZoom();
};

const navigateGallery = (direction) => {
    if (galleryList.value.length <= 1) return;
    const next = currentIndex.value + direction;
    if (next < 0 || next >= galleryList.value.length) return;
    selectItem(galleryList.value[next], next);
};

const close = () => {
    uiStore.closeMediaViewer();

    setTimeout(() => {
        resetZoom();
    }, 300);
};

const handleOverlayClick = () => {
    close();
};

const resetZoom = () => {
    isZoomed.value = false;
    zoomOrigin.value = 'center center';
};

const downloadMedia = async () => {
    const url = media.value.url;
    const rawFilename = media.value.filename;
    const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
    const filename = rawFilename || (ext && ext.length <= 4 ? `file.${ext}` : 'file');

    try {
        const response = await fetch(media.value.url, { mode: 'cors', cache: 'no-store' }); 
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blobUrl = URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    } catch (_) {

        window.open(url, '_blank');
    }
}

const toggleZoom = (event) => {
    if (!isZoomed.value) {
        updateZoomPosition(event);
        isZoomed.value = true;
    } else {
        isZoomed.value = false;
    }
};

const handleMouseMove = (event) => {
    if (isZoomed.value) {
        updateZoomPosition(event);
    }
};

const handleMouseLeave = () => {
    //
};

const updateZoomPosition = (event) => {
    if (!imageRef.value) return;
    const rect     = imageRef.value.getBoundingClientRect();
    const x        = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width)  * 100));
    const y        = Math.max(0, Math.min(100, ((event.clientY - rect.top)  / rect.height) * 100));
    zoomOrigin.value = `${x}% ${y}%`;
};

const handleKeydown = (e) => {
    if (!isOpen.value) return;
    if (e.key === 'Escape')      close();
    if (e.key === 'ArrowRight')  navigateGallery(1);
    if (e.key === 'ArrowLeft')   navigateGallery(-1);
};

// Variáveis para Swipe (Mobile)
const touchStartX = ref(0);
const touchEndX = ref(0);
const minSwipeDistance = 50; // Distância mínima para considerar um swipe

const handleTouchStart = (e) => {
    touchStartX.value = e.changedTouches[0].screenX;
};

const handleTouchEnd = (e) => {
    touchEndX.value = e.changedTouches[0].screenX;
    handleSwipeGesture();
};

const handleSwipeGesture = () => {
    // Evita trocar a mídia se o usuário estiver dando zoom/movendo a imagem
    if (isZoomed.value) return; 
    
    const distance = touchEndX.value - touchStartX.value;
    
    if (distance < -minSwipeDistance) {
        navigateGallery(1); // Swipe esquerda (Avançar)
    } else if (distance > minSwipeDistance) {
        navigateGallery(-1); // Swipe direita (Voltar)
    }
};

onMounted(() => window.addEventListener('keydown', handleKeydown));
onUnmounted(() => window.removeEventListener('keydown', handleKeydown));
</script>

<style scoped>
.carousel-arrow {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3000;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 26px;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.carousel-arrow:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-50%) scale(1.08);
}

.carousel-arrow-left  { left: 20px; }
.carousel-arrow-right { right: 20px; }

.media-viewer-overlay {
    position: fixed;
    top: 0; left: 0; width: 100vw; height: 100vh;
    background-color: rgb(11 20 26 / 29%);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    backdrop-filter: blur(5px);
}

@media (max-width: 991.98px) {
    .carousel-arrow {
        display: none !important;
    }
}

.floating-actions {
    position: absolute;
    top: 20px;
    right: 20px;
    display: flex;
    gap: 15px;
    z-index: 20;
}

.btn-float {
    background: rgba(0, 0, 0, 0.4);
    border: none;
    color: #aebac1;
    font-size: 24px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
}
.btn-float:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #fff;
}

.viewer-body {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 0;
    width: 100%;
    height: 100%;
}

.image-wrapper {
    transition: transform 0.3s ease;
    display: flex;
    justify-content: center;
    align-items: center;
}

.media-content {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 10px; 
    box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    transition: transform 0.3s ease-out;
}

.video-content {
    border-radius: 10px;
    max-height: 80vh;
}

.gallery-footer {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding: 10px 0 20px 0;
    background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
    z-index: 20;
}

.thumbnails-container {
    display: flex;
    justify-content: center;
    gap: 8px;
    overflow-x: auto;
    padding: 5px 20px;
    scrollbar-width: none;
    scroll-behavior: smooth; /* Anima a rolagem via JS */
}
.thumbnails-container::-webkit-scrollbar {
    display: none;
}

.thumb-wrapper {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    overflow: hidden;
    opacity: 0.5;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.2s;
    flex-shrink: 0;
    position: relative;
    background-color: #000;
}

.thumb-wrapper img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.video-thumb {
    width: 100%;
    height: 100%;
    position: relative;
}
.video-thumb video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.video-thumb .play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 22px;
    opacity: 0.9;
    text-shadow: 0px 2px 4px rgba(0,0,0,0.6);
}

.thumb-wrapper.active {
    opacity: 1;
    border-color: var(--bs-success);
    transform: scale(1.1);
}

.thumb-wrapper:hover {
    opacity: 0.8;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.media-caption-container {
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    text-align: center;
    margin-bottom: 12px;
    padding: 0 20px;
    line-height: 1.5;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8); 
    max-height: 20vh;
    overflow-y: auto;
}
</style>